import React, { createContext, useContext, useEffect, useState } from 'react'
import { useNavigationType, useLocation } from 'react-router-dom'

/**
 * Defines the context for preventing navigation.
 * Provides a way to set a message that will be shown to the user when they try to navigate away from the page.
 */
interface IPreventNavigationContext {
  /** Message to show when preventing navigation */
  message?: string
  /** Function to set the message to prevent navigation */
  setMessageToPrevent: (message?: string) => void
}

/** Context for preventing navigation */
const PreventNavigationContext = createContext<IPreventNavigationContext>({
  setMessageToPrevent: () => {}
})

/** Provider component for PreventNavigationContext
 * This component manages the state of the message to prevent navigation
 */
const PreventNavigationProvider: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  /** State to hold the message to prevent navigation */
  const [message, setMessage] = useState<string>()
  /** Hook to get the type of navigation (e.g., POP, PUSH) */
  const navigationType = useNavigationType()
  /** Hook to get the current location */
  const location = useLocation()

  const setMessageToPrevent = (message?: string) => {
    setMessage(message)
  }

  /** Effect to handle before unload event
   * This effect sets up a listener for the beforeunload event to prevent navigation
   */
  useEffect(() => {
    /**
     * Function to handle beforeunload event
     * This function prevents the user from navigating away if a message is set
     */
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (message) {
        event.preventDefault()
        event.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [message])

  /** Effect to handle navigation changes
   * This effect checks the navigation type and shows a confirmation dialog if the message is set
   */
  useEffect(() => {
    if (navigationType == 'POP' && message) {
      const confirmExit = window.confirm(message)
      if (!confirmExit) {
        window.history.pushState(null, '', location.pathname)
      }
    } else {
      setMessage(undefined)
    }
  }, [location, navigationType])

  return (
    <PreventNavigationContext.Provider value={{ setMessageToPrevent, message }}>
      {children}
    </PreventNavigationContext.Provider>
  )
}

/**
 * Hook to access the context value for preventing navigation
 * @returns IPreventNavigationContext
 */
export function usePreventNavigation() {
  return useContext(PreventNavigationContext)
}

export default PreventNavigationProvider
